import { useEffect, useId, useState } from 'react';
import { useCurrentUser } from '../account/useAuth';
import * as projectsApi from '../account/projectsApi';
import { Button } from '../components/Button/Button';
import type { Class, Project, Sample } from '../data/types';
import { bucketLength, capture } from '../lib/analytics';
import type { SubmissionStatus } from './galleryApi';
import { useSubmitToGallery, useUploadBlob } from './useGallery';
import './PublishDialog.css';

/**
 * Publish-to-gallery flow.
 *
 * Distinct from `SyncButton` (which only mirrors metadata) and from
 * `ShipDialog` (which exports a static PWA — no server). Publishing
 * uploads a representative sample per class to the server, then
 * creates a `pending` gallery submission. The moderation pipeline
 * runs server-side and flips the submission to `approved` or
 * `rejected` asynchronously.
 *
 * UI rules:
 * - Only signed-in kids see the button (the parent of this dialog
 *   handles that gate).
 * - The confirmation copy is explicit about what's leaving the
 *   device. CLAUDE.md's "Privacy is visible" rule.
 * - The kid sees status updates as the upload + submit chain runs
 *   so they don't sit on a spinner.
 */

export interface PublishDialogProps {
  isOpen: boolean;
  /** The local Project. Already exists in IndexedDB. */
  project: Project;
  /** The kid's classes. We upload one sample per class. */
  classes: Class[];
  /** Per-class samples, keyed by class id. */
  samplesByClass: Record<string, { byId: Sample[] } | undefined>;
  onClose: () => void;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'syncing' }
  | { kind: 'uploading'; doneCount: number; total: number }
  | { kind: 'submitting' }
  | { kind: 'submitted'; submissionId: string; status: SubmissionStatus }
  | { kind: 'error'; message: string };

export function PublishDialog({
  isOpen,
  project,
  classes,
  samplesByClass,
  onClose,
}: PublishDialogProps): JSX.Element | null {
  const titleId = useId();
  const { data: user } = useCurrentUser();
  const submit = useSubmitToGallery();
  const upload = useUploadBlob();
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [description, setDescription] = useState('');
  const descriptionId = useId();

  useEffect(() => {
    if (!isOpen) {
      setPhase({ kind: 'idle' });
      setDescription('');
      submit.reset();
      upload.reset();
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  // Build an upload plan: one representative sample per non-empty
  // class. We pick the most recent sample so the kid's latest work
  // is what reviewers see.
  const plan = classes
    .map((cls) => {
      const samples = samplesByClass[cls.id]?.byId ?? [];
      const newest = [...samples].sort((a, b) => b.createdAt - a.createdAt)[0];
      return newest ? { classId: cls.id, className: cls.name, sample: newest } : null;
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  const totalUploads = plan.length;
  const noSamples = totalUploads === 0;
  const projectKind = project.kind;
  // The backend accepts `image` and `audio` blob kinds. Pose
  // projects share the image storage path because the bytes are
  // still PNGs.
  const blobKind: 'image' | 'audio' = projectKind === 'audio' ? 'audio' : 'image';

  const handleConfirm = async (): Promise<void> => {
    if (noSamples) return;
    capture('gallery_submission_started', {
      kind: project.kind,
      class_count: plan.length,
    });
    try {
      // 1. Ensure the server project exists. We always create a fresh
      //    one so the published version is a snapshot (not a live
      //    mirror that changes when the kid keeps editing locally).
      setPhase({ kind: 'syncing' });
      const serverProject = await projectsApi.createServerProject({
        name: project.name,
        kind: project.kind,
        block_program: project.blockProgram ?? null,
      });

      // 2. Upload one sample per class.
      let done = 0;
      setPhase({ kind: 'uploading', doneCount: done, total: totalUploads });
      for (const row of plan) {
        await upload.mutateAsync({
          projectId: serverProject.id,
          input: {
            classLabel: row.className,
            kind: blobKind,
            file: row.sample.blob,
            fileName: `${row.className}-${row.sample.id}`,
          },
        });
        done += 1;
        setPhase({ kind: 'uploading', doneCount: done, total: totalUploads });
      }

      // 3. Submit.
      setPhase({ kind: 'submitting' });
      const trimmed = description.trim();
      const result = await submit.mutateAsync({
        projectId: serverProject.id,
        ...(trimmed ? { description: trimmed } : {}),
      });
      capture('gallery_submission_sent', {
        kind: project.kind,
        class_count: plan.length,
        description_length_bucket: bucketLength(trimmed),
        submission_status: result.status,
      });
      setPhase({ kind: 'submitted', submissionId: result.id, status: result.status });
    } catch (err) {
      setPhase({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Could not publish.',
      });
    }
  };

  const busy =
    phase.kind === 'syncing' || phase.kind === 'uploading' || phase.kind === 'submitting';

  return (
    <div
      className="bl-publish__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="bl-publish__dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId} className="bl-publish__title">
          Publish to the gallery
        </h2>

        {phase.kind === 'submitted' ? (
          <SubmittedView
            submissionId={phase.submissionId}
            status={phase.status}
            onClose={onClose}
            audience={resolveAudience(user)}
          />
        ) : (
          <>
            <p className="bl-publish__lead">{leadCopy(user)}</p>

            {!user && (
              <p className="bl-publish__warn" role="alert">
                Sign in first — only signed-in kids can publish.
              </p>
            )}

            {noSamples && user && (
              <p className="bl-publish__warn" role="alert">
                Add at least one sample to a class before publishing — there&rsquo;s nothing to show
                off yet!
              </p>
            )}

            {plan.length > 0 && (
              <ul className="bl-publish__plan" aria-label="Samples to send">
                {plan.map((row) => (
                  <li key={row.classId}>
                    <strong>{row.className}</strong>
                    <span>1 sample · {projectKind} project</span>
                  </li>
                ))}
              </ul>
            )}

            {plan.length > 0 && (
              <div className="bl-publish__field">
                <label htmlFor={descriptionId}>
                  Tell visitors what your model does
                  <span className="bl-publish__hint"> (optional)</span>
                </label>
                <textarea
                  id={descriptionId}
                  className="bl-publish__textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 280))}
                  placeholder="A high-five detector for my brother."
                  rows={2}
                  maxLength={280}
                  disabled={busy}
                />
                <p className="bl-publish__counter" aria-live="polite">
                  {description.length}/280
                </p>
              </div>
            )}

            {phase.kind === 'syncing' && (
              <p className="bl-publish__status" aria-live="polite">
                Syncing your project to the server…
              </p>
            )}
            {phase.kind === 'uploading' && (
              <p className="bl-publish__status" aria-live="polite">
                Uploading samples ({phase.doneCount}/{phase.total})…
              </p>
            )}
            {phase.kind === 'submitting' && (
              <p className="bl-publish__status" aria-live="polite">
                Sending for review…
              </p>
            )}
            {phase.kind === 'error' && (
              <p className="bl-publish__error" role="alert">
                {phase.message}
              </p>
            )}

            <div className="bl-publish__actions">
              <Button variant="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={() => void handleConfirm()} disabled={busy || !user || noSamples}>
                {busy ? 'Working…' : 'Publish'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Three publish audiences. The kid sees different status copy for
 * each because the moderation path is genuinely different:
 *
 *   * `classroom`  — class-only submission. Goes to the teacher's
 *                    review queue first.
 *   * `parent`     — parent-linked child. Triggers a parent-consent
 *                    magic link before any moderation.
 *   * `public`     — adult (or pre-classroom) account. Goes straight
 *                    to BytaApps's moderation pipeline.
 */
type PublishAudience = 'classroom' | 'parent' | 'public';

function resolveAudience(
  user: { is_child?: boolean; classroom_id?: string | null } | null | undefined,
): PublishAudience {
  if (!user) return 'public';
  if (user.classroom_id) return 'classroom';
  if (user.is_child) return 'parent';
  return 'public';
}

function leadCopy(
  user: { is_child?: boolean; classroom_id?: string | null } | null | undefined,
): string {
  switch (resolveAudience(user)) {
    case 'classroom':
      return 'Publishing sends your project to your teacher for review. Your classmates can react when your teacher approves it. The rest of your samples stay on this device.';
    case 'parent':
      return 'Sharing to the gallery needs a grown-up to say yes first. We send them an email with a link — they tap it, then a grown-up at BytaApps does the safety check.';
    case 'public':
      return 'Sharing your project to the gallery sends one sample from each class — plus the project name and labels — to a grown-up at BytaApps who’ll review it before anyone else can see it. The rest of your samples stay on this device.';
  }
}

interface SubmittedViewProps {
  submissionId: string;
  status: SubmissionStatus;
  onClose: () => void;
  audience: PublishAudience;
}

function SubmittedView({ status, onClose, audience }: SubmittedViewProps): JSX.Element {
  if (audience === 'classroom') {
    return (
      <>
        <p className="bl-publish__lead">
          Sent! Your teacher will see this in their review queue. Once they approve, it shows up in
          your class gallery and your classmates can react.
        </p>
        <p className="bl-publish__lead">
          Watch the status from the <em>Feedback</em> page — your teacher might leave a note about
          what you did well.
        </p>
        <div className="bl-publish__actions">
          <Button onClick={onClose}>Got it</Button>
        </div>
      </>
    );
  }
  if (audience === 'parent') {
    return (
      <>
        <p className="bl-publish__lead">
          Sent! Your grown-up will get an email with a link. Once they click <em>yes, share it</em>,
          a grown-up at BytaApps does a safety check, and then it&rsquo;s up.
        </p>
        <p className="bl-publish__lead">
          If your grown-up says no, the project stays on this device and nobody else sees it.
        </p>
        <div className="bl-publish__actions">
          <Button onClick={onClose}>Got it</Button>
        </div>
      </>
    );
  }
  // Public / adult audience — copy depends on whether the backend
  // auto-approved at submit time. The default-pass policy means most
  // submissions land here; "pending" / "awaiting_moderator" only when
  // an auto-screening provider is wired up and is still deciding.
  if (status === 'approved') {
    return (
      <>
        <p className="bl-publish__lead">
          You&rsquo;re live! 🎉 Your project is in the gallery and other kids can browse and remix
          it now.
        </p>
        <p className="bl-publish__lead">
          A grown-up at Bryta will spot-check it. If anything&rsquo;s off they may take it down and
          let you know — but you don&rsquo;t have to wait around. Your training samples stay private
          on this device.
        </p>
        <div className="bl-publish__actions">
          <Button onClick={onClose}>Got it</Button>
        </div>
      </>
    );
  }
  return (
    <>
      <p className="bl-publish__lead">
        Sent! A grown-up at Bryta will review your project. You can watch the status from the studio
        — it&rsquo;ll change from <em>pending</em> to either <em>approved</em> or <em>rejected</em>{' '}
        when the review&rsquo;s done.
      </p>
      <p className="bl-publish__lead">
        While we&rsquo;re looking at it, your samples stay private — no one else can see them.
      </p>
      <div className="bl-publish__actions">
        <Button onClick={onClose}>Got it</Button>
      </div>
    </>
  );
}
